import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Tournament {
  id: string;
  title: string;
  sport: string;
  location: string;
  start_date: string;
  end_date: string | null;
  status: string;
  event_type: string;
}

export function useUserTournaments() {
  const { user } = useAuth();
  const [tournamentsCount, setTournamentsCount] = useState(0);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTournaments = async () => {
    if (!user) return;

    try {
      // Get count
      const { count, error: countError } = await supabase
        .from('registrations')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('status', 'confirmed');

      if (countError) throw countError;
      setTournamentsCount(count || 0);

      // Get detailed tournament data
      const { data, error } = await supabase
        .from('registrations')
        .select(`
          events(
            id,
            title,
            sport,
            location,
            start_date,
            end_date,
            status,
            event_type
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'confirmed')
        .order('registered_at', { ascending: false });

      if (error) throw error;

      const tournamentsData: Tournament[] = data?.map(item => item.events).filter(Boolean) || [];
      setTournaments(tournamentsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tournaments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchTournaments();
    }
  }, [user]);

  return {
    tournamentsCount,
    tournaments,
    loading,
    error,
    refetch: fetchTournaments
  };
}